import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { Terminal, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TerminalEntry {
  type: 'input' | 'output' | 'error';
  content: string;
  timestamp: Date;
}

export default function DirectTerminal() {
  const { toast } = useToast();
  const [command, setCommand] = useState('');
  const [history, setHistory] = useState<TerminalEntry[]>([
    {
      type: 'output',
      content: 'Agent Control Terminal v1.0\nType "help" for available commands',
      timestamp: new Date()
    }
  ]);
  const [executing, setExecuting] = useState(false);

  const addEntry = (type: TerminalEntry['type'], content: string) => {
    setHistory(prev => [...prev, { type, content, timestamp: new Date() }]);
  };

  const executeCommand = async () => {
    if (!command.trim()) return;
    
    addEntry('input', `$ ${command}`);
    setExecuting(true);

    try {
      const parts = command.trim().split(' ');
      const cmd = parts[0];
      const args = parts.slice(1);

      switch (cmd) {
        case 'help':
          addEntry('output', `Available commands:
  help                          Show this help message
  tools list                    List all available tools
  tools enable <name>           Enable a tool
  tools disable <name>          Disable a tool
  memory list                   List all preferences
  memory set <key> <value>      Set a preference
  prompt show                   Show current system prompt
  voice config                  Show voice configuration
  invoke <function> <json>      Directly invoke an edge function`);
          break;

        case 'tools':
          if (args[0] === 'list') {
            addEntry('output', 'Fetching tool configuration...');
            // Implementation would call agent-memory-service
            addEntry('output', 'Tools: createJob, createLead, getDailyBriefing, etc.');
          }
          break;

        case 'memory':
          if (args[0] === 'list') {
            addEntry('output', 'Fetching memory...');
            const { data } = await supabase.functions.invoke('agent-memory-service', {
              body: { action: 'get_preferences', data: {} }
            });
            addEntry('output', JSON.stringify(data, null, 2));
          }
          break;

        case 'invoke':
          if (args.length < 2) {
            addEntry('error', 'Usage: invoke <function-name> <json-payload>');
            break;
          }
          const functionName = args[0];
          const payload = JSON.parse(args.slice(1).join(' '));
          addEntry('output', `Invoking ${functionName}...`);
          
          const { data, error } = await supabase.functions.invoke(functionName, {
            body: payload
          });
          
          if (error) {
            addEntry('error', `Error: ${error.message}`);
          } else {
            addEntry('output', JSON.stringify(data, null, 2));
          }
          break;

        case 'clear':
          setHistory([{
            type: 'output',
            content: 'Terminal cleared',
            timestamp: new Date()
          }]);
          break;

        default:
          addEntry('error', `Unknown command: ${cmd}. Type "help" for available commands.`);
      }
    } catch (error) {
      addEntry('error', `Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setExecuting(false);
      setCommand('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      executeCommand();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Terminal className="w-5 h-5" />
          Direct Command Terminal
        </CardTitle>
        <CardDescription>
          Execute commands directly against the agent system. Advanced users only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScrollArea className="h-[500px] rounded-lg bg-black p-4 font-mono text-sm">
          <div className="space-y-2">
            {history.map((entry, idx) => (
              <div
                key={idx}
                className={`${
                  entry.type === 'input'
                    ? 'text-green-400'
                    : entry.type === 'error'
                    ? 'text-red-400'
                    : 'text-gray-300'
                }`}
              >
                <pre className="whitespace-pre-wrap">{entry.content}</pre>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex gap-2">
          <Input
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter command..."
            disabled={executing}
            className="font-mono"
          />
          <Button
            onClick={executeCommand}
            disabled={executing || !command.trim()}
            className="flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Execute
          </Button>
        </div>

        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-sm text-amber-600 dark:text-amber-400">
            <strong>Warning:</strong> Direct terminal access can modify agent configuration.
            Use with caution and ensure you understand the commands you're executing.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
